import { DateTime } from 'luxon';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Breadcrumb from '@ui/atoms/Breadcrumb';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import Toast from '@ui/atoms/Toast';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import IFrameContainer from '@ui/molecules/IFrameContainer';
import MessageIconCard from '@ui/molecules/MessageIconCard';
import ReadMore from '@ui/molecules/ReadMore';

import BookingUnavailable from '@ui/organisms/BookingUnavailable/BookingUnavailable.component';

import fullWidth from '@ui/styled/FullWidth';

import { MemberInfo } from '@hooks/useMemberInfo';

import { showAppointmentToast } from '@helpers/Appointments/AppointmentHelpers';

import { interpolate } from '@utils/butter';

import { IUnscheduledAppointment } from '@folx-types/member';

import CheckCircle from '@assets/vectors/check-circle-solid.inline.svg';

import theme from '@theme/FolxTheme';

import PastCancelDateAppointmentError from './PastCancelDateAppointmentError.component';

export interface IRescheduleAppointContent {
	cancelHeader: string;
	rescheduleHeader: string;
	appointmentBreadcrumb: string;
	appointmentDisclaimer: string;
	appointmentSubheader: string;
	appointmentLoading: string;
	confirmationHeader: string;
	confirmationDescription: string;
	cancellationHeader: string;
	cancellationDescription: string;
	pastMembershipCancelDate: {
		cta: string;
		description: string;
		header: string;
		secondaryCta: string;
		subheader: string;
	};
	errorMessages: {
		contactToReschedule: string;
	};
}

interface IRescheduleAppointmentProps {
	apptDate: string;
	loading: boolean;
	pageContent: IRescheduleAppointContent;
	isPastCancelledDate: boolean;
	memberInfo?: MemberInfo;
	rescheduled?: boolean;
	selectedAppointment?: IUnscheduledAppointment;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);

const CancellationPolicy = styled(ReadMore)`
	padding: ${theme.spacing.smmd};
`;

const RescheduleContainer = styled.div`
	height: 78rem;
`;

const RescheduleAppointment: FC<IRescheduleAppointmentProps> = ({
	loading,
	pageContent,
	rescheduled,
	selectedAppointment,
	isPastCancelledDate,
	apptDate,
	memberInfo,
}) => {
	const {
		cancelHeader,
		rescheduleHeader,
		appointmentBreadcrumb,
		appointmentDisclaimer,
		appointmentSubheader,
		confirmationHeader,
		confirmationDescription,
		cancellationHeader,
		cancellationDescription,
		pastMembershipCancelDate,
		errorMessages,
	} = pageContent;

	const [shouldReschedule, setShouldReschedule] = useState<boolean>(false);

	const convertedDate = DateTime.fromISO(selectedAppointment?.startTime ?? '');
	const convertedHours = convertedDate?.diffNow('hours').hours;

	const isWithinCancelWindow = convertedHours > 24;
	const allowCancelAppointment = isWithinCancelWindow;

	const appointmentType = 'Rescheduled';

	useEffect(() => {
		showAppointmentToast(memberInfo?.stateOfResidence?.abbreviation);
	}, [memberInfo?.stateOfResidence?.abbreviation]);

	const scheduleAppointmentView = (
		<>
			<LoadingWrapper
				loading={loading}
				skeleton={<Skeleton structure="single" height="lg" fullWidth />}>
				<HeadingText>{allowCancelAppointment ? cancelHeader : rescheduleHeader}</HeadingText>
			</LoadingWrapper>
			<BookingUnavailable>
				<RescheduleContainer>
					{loading && <Skeleton fullHeight fullWidth structure="single" />}
					{!loading && selectedAppointment?.rescheduleUrl && (
						<IFrameContainer
							iFrameStyle={{ height: '100%', width: '100%' }}
							url={selectedAppointment?.rescheduleUrl}
							loading={loading}
						/>
					)}
				</RescheduleContainer>
			</BookingUnavailable>
			<BasicText variant="fineprint">{appointmentDisclaimer}</BasicText>
			<CancellationPolicy label={cancellationHeader}>
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: cancellationDescription }} />
			</CancellationPolicy>
		</>
	);

	const scheduledView = () => {
		const localizedDate = convertedDate?.toFormat('EEEE, MMMM d');
		const localizedTime = convertedDate?.toFormat('h:mm a ZZZZ');

		return (
			<LoadingWrapper
				loading={loading}
				skeleton={
					<>
						<Skeleton structure="single" height="lg" fullWidth />
						<Skeleton structure="single" height="xxxxl" fullWidth />
					</>
				}>
				<FlexColumn gap="sm">
					<HeadingText fontSize="sm">{confirmationHeader}</HeadingText>
					<MessageIconCard
						bgColor="greenLight"
						message={confirmationDescription}
						Icon={CheckCircle}
						iconAlt="checkmark"
					/>
					<FlexColumn gap="xssm">
						<BasicText variant="small">
							{interpolate(appointmentSubheader, { appointmentType })}
						</BasicText>
						<BasicText variant="body">{`${localizedDate} at ${localizedTime}`}</BasicText>
					</FlexColumn>
				</FlexColumn>
			</LoadingWrapper>
		);
	};

	const unableToRescheduleView = () => (
		<LoadingWrapper
			loading={loading}
			skeleton={
				<>
					<Skeleton structure="single" height="lg" fullWidth />
					<Skeleton structure="single" height="xxxxl" fullWidth />
				</>
			}>
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: errorMessages.contactToReschedule }} />
		</LoadingWrapper>
	);

	const determineView = () => {
		if (isPastCancelledDate && !shouldReschedule) {
			return (
				<PastCancelDateAppointmentError
					appointmentType={appointmentType}
					convertedDate={DateTime.fromISO(apptDate)}
					onClick={() => setShouldReschedule(true)}
					{...pastMembershipCancelDate}
				/>
			);
		} else if (rescheduled && !shouldReschedule) {
			return scheduledView();
		} else if (!selectedAppointment?.rescheduleUrl) {
			return unableToRescheduleView();
		} else {
			return scheduleAppointmentView;
		}
	};

	return (
		<FullWidthColumn gap="md">
			<Breadcrumb label={appointmentBreadcrumb} href="/app/member/appointments" />
			{determineView()}
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
		</FullWidthColumn>
	);
};

export default RescheduleAppointment;
